/**
 * AI による睡眠の解析と、数値から言えること（hints）
 */

import { AlertCircle, CheckCircle2, Lightbulb, MinusCircle, RefreshCw, Sparkles } from 'lucide-react'

import type { SleepAiReport } from '@/api/types/sleep'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

type Props = {
  ai: SleepAiReport | null
  aiModel: string | null
  aiStale: boolean
  hints: string[]
  isAnalyzing: boolean
  onAnalyze: () => void
}

export const ToneIcon = ({ tone }: { tone: string }) => {
  if (tone === 'good') return <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
  if (tone === 'bad') return <AlertCircle className="mt-0.5 size-4 shrink-0 text-rose-500" />
  return <MinusCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
}

const HintList = ({ hints }: { hints: string[] }) => (
  <ul className="space-y-1.5">
    {hints.map((hint) => (
      <li key={hint} className="flex gap-2 text-sm">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-500" />
        <span>{hint}</span>
      </li>
    ))}
  </ul>
)

export const AiReportCard = ({ ai, aiModel, aiStale, hints, isAnalyzing, onAnalyze }: Props) => (
  <section className="space-y-3 rounded-xl border bg-card p-4 shadow-sm">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-indigo-500" />
        <span className="text-sm font-medium">解析</span>
        {aiModel && <span className="text-[10px] text-muted-foreground">{aiModel}</span>}
      </div>
      <Button size="sm" variant="outline" onClick={onAnalyze} disabled={isAnalyzing}>
        {isAnalyzing ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
        {ai ? '再解析' : 'AI で解析'}
      </Button>
    </div>

    {aiStale && (
      <p className="rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
        解析後に Garmin 側のデータが更新されています
      </p>
    )}

    {ai ? (
      <div className="space-y-3">
        <div>
          <p className="font-semibold">{ai.headline}</p>
          <p className="mt-1 text-sm text-muted-foreground">{ai.summary}</p>
        </div>

        <ul className="space-y-2">
          {ai.findings.map((f) => (
            <li key={f.title} className="flex gap-2">
              <ToneIcon tone={f.tone} />
              <div>
                <p className="text-sm font-medium">{f.title}</p>
                <p className="text-xs text-muted-foreground">{f.detail}</p>
              </div>
            </li>
          ))}
        </ul>

        {ai.actions_today.length > 0 && (
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">次に寝るまでに</p>
            <ul className="list-disc space-y-1 pl-4 text-sm">
              {ai.actions_today.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-lg bg-indigo-500/10 p-3">
          <p className="mb-1 text-xs font-medium text-indigo-600 dark:text-indigo-300">次の睡眠</p>
          <p className="text-sm">{ai.next_sleep}</p>
        </div>
      </div>
    ) : (
      !isAnalyzing && <p className="text-sm text-muted-foreground">まだ AI で解析していません</p>
    )}

    {hints.length > 0 && (
      <div className={cn('space-y-2', ai && 'border-t pt-3')}>
        <p className="text-xs font-medium text-muted-foreground">データから言えること</p>
        <HintList hints={hints} />
      </div>
    )}
  </section>
)
